import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { GALLERY } from "../data/site";

const GALLERY_I18N = {
  en: { kicker: "Gallery", title: "A peek around the house", sub: "Swipe through our rooms, common spaces and the beach a two-minute walk away." },
  es: { kicker: "Galería", title: "Un vistazo por la casa", sub: "Desliza por nuestras habitaciones, espacios comunes y la playa a dos minutos caminando." },
  fr: { kicker: "Galerie", title: "Un aperçu de la maison", sub: "Faites défiler nos chambres, espaces communs et la plage à deux minutes à pied." },
  de: { kicker: "Galerie", title: "Ein Blick durchs Haus", sub: "Wische durch unsere Zimmer, Gemeinschaftsräume und den Strand — zwei Gehminuten entfernt." },
  ru: { kicker: "Галерея", title: "Загляните в наш дом", sub: "Пролистайте наши номера, общие зоны и пляж в двух минутах ходьбы." },
  zh: { kicker: "相册", title: "参观我们的家", sub: "滑动浏览我们的客房、公共空间，以及步行两分钟即达的海滩。" },
  it: { kicker: "Galleria", title: "Uno sguardo alla casa", sub: "Scorri tra le nostre camere, gli spazi comuni e la spiaggia a due minuti a piedi." },
  pt: { kicker: "Galeria", title: "Uma espreitadela pela casa", sub: "Deslize pelos nossos quartos, espaços comuns e a praia a dois minutos a pé." },
};

export const Gallery = () => {
  const { lang } = useLang();
  const g = GALLERY_I18N[lang] || GALLERY_I18N.en;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", dragFree: true });
  const [lightbox, setLightbox] = useState(null);
  const swipeStart = useRef({ x: 0, y: 0 });
  const swiped = useRef(false);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const openAt = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const prev = useCallback(() => setLightbox((i) => (i === null ? i : (i - 1 + GALLERY.length) % GALLERY.length)), []);
  const next = useCallback(() => setLightbox((i) => (i === null ? i : (i + 1) % GALLERY.length)), []);

  // Swipe navigation (works for touch, mouse & pen). Handled on the overlay so the
  // gesture is captured anywhere; a real swipe suppresses the tap-to-close click.
  const onSwipeStart = (e) => {
    swipeStart.current = { x: e.clientX, y: e.clientY };
    swiped.current = false;
  };
  const onSwipeEnd = (e) => {
    const dx = e.clientX - swipeStart.current.x;
    const dy = e.clientY - swipeStart.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      swiped.current = true;
      if (dx < 0) next();
      else prev();
    }
  };
  const onBackdropClick = () => {
    if (swiped.current) {
      swiped.current = false;
      return;
    }
    close();
  };

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, prev, next]);

  return (
    <section id="gallery" className="overflow-hidden bg-sand py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-12 max-w-2xl">
          <span className="text-xs font-medium uppercase tracking-[0.3em] text-teal">{g.kicker}</span>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-purpleink-deep sm:text-5xl">
            {g.title}
          </h2>
          <p className="mt-4 text-lg text-stone-600">{g.sub}</p>
        </div>
      </div>

      <div className="cursor-grab overflow-hidden active:cursor-grabbing" ref={emblaRef}>
        <div className="flex gap-4 pl-5 sm:pl-8">
          {GALLERY.map((img, i) => (
            <button
              key={i}
              onClick={() => openAt(i)}
              data-testid={`gallery-slide-${i}`}
              className={`group relative flex-[0_0_78%] overflow-hidden rounded-[1.5rem] sm:flex-[0_0_46%] lg:flex-[0_0_31%] ${
                img.tall ? "aspect-[3/4]" : "aspect-[4/3]"
              }`}
            >
              <img
                src={img.src}
                alt={`Casa Romano photo ${i + 1}`}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-purpleink-deep/0 transition-colors duration-300 group-hover:bg-purpleink-deep/25" />
              <span className="absolute bottom-3 right-3 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-white/90 text-purpleink opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-4 px-5 sm:px-8">
        <button
          onClick={scrollPrev}
          data-testid="gallery-prev"
          aria-label="Previous"
          className="flex h-14 w-14 items-center justify-center rounded-full border border-purpleink/20 text-purpleink transition-colors hover:bg-purpleink hover:text-white active:scale-95"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={scrollNext}
          data-testid="gallery-next"
          aria-label="Next"
          className="flex h-14 w-14 items-center justify-center rounded-full border border-purpleink/20 text-purpleink transition-colors hover:bg-purpleink hover:text-white active:scale-95"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-purpleink-deep/95 p-4 backdrop-blur-sm"
            onClick={onBackdropClick}
            onPointerDown={onSwipeStart}
            onPointerUp={onSwipeEnd}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
              aria-label="Previous"
              className="absolute left-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-8"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
              aria-label="Next"
              className="absolute right-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-8"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              src={GALLERY[lightbox].src}
              alt={`Casa Romano photo ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
              style={{ touchAction: "pan-y" }}
              data-testid="lightbox-image"
              className="max-h-[85vh] max-w-[90vw] select-none rounded-2xl object-contain shadow-2xl"
            />
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-white">
              {lightbox + 1} / {GALLERY.length}
              <span className="hidden text-white/50 sm:inline">· swipe or use arrows</span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
